import React, { useEffect } from "react";
import { Col, Row } from "reactstrap";
import { Switch } from "@mui/material";
import CodeSelectDropdown from "../common/codeSelectDropdown";
import {
  ICodeModel,
  IExtraGiftFieldSetting,
  IDonorPerfectExtraGiftFields,
} from "../../../../types/services/DonorPerfectTypes";
import styles from "./styles.scss";

export type ExtraGiftFieldKey = "generalLedger" | "campaign" | "thankYouLetter";

export interface ExtraGiftFieldsState {
  generalLedger: IExtraGiftFieldSetting;
  campaign: IExtraGiftFieldSetting;
  thankYouLetter: IExtraGiftFieldSetting;
}

export const createDefaultExtraGiftFields = (): ExtraGiftFieldsState => ({
  generalLedger: { enabled: false, code: "", label: "" },
  campaign: { enabled: false, code: "", label: "" },
  thankYouLetter: { enabled: false, code: "", label: "" },
});

export const mapConfigExtraGiftFields = (
  from?: IDonorPerfectExtraGiftFields
): ExtraGiftFieldsState => {
  const defaults = createDefaultExtraGiftFields();
  if (!from) {
    return defaults;
  }
  return {
    generalLedger: {
      enabled: !!from.generalLedger?.enabled,
      code: from.generalLedger?.code || "",
      label: from.generalLedger?.label || "",
    },
    campaign: {
      enabled: !!from.campaign?.enabled,
      code: from.campaign?.code || "",
      label: from.campaign?.label || "",
    },
    thankYouLetter: {
      enabled: !!from.thankYouLetter?.enabled,
      code: from.thankYouLetter?.code || "",
      label: from.thankYouLetter?.label || "",
    },
  };
};

export const isExtraGiftFieldsValid = (
  fields: ExtraGiftFieldsState
): boolean => {
  const fieldValid = (setting: IExtraGiftFieldSetting) =>
    !setting.enabled || Boolean(setting.code);
  return (
    fieldValid(fields.generalLedger) &&
    fieldValid(fields.campaign) &&
    fieldValid(fields.thankYouLetter)
  );
};

export const hasExtraGiftFieldsSelection = (
  fields: ExtraGiftFieldsState
): boolean =>
  fields.generalLedger.enabled ||
  fields.campaign.enabled ||
  fields.thankYouLetter.enabled;

export const buildExtraGiftFieldsPayload = (
  fields: ExtraGiftFieldsState
): IDonorPerfectExtraGiftFields => ({
  generalLedger: {
    enabled: fields.generalLedger.enabled,
    code: fields.generalLedger.enabled ? fields.generalLedger.code : "",
    label: fields.generalLedger.enabled ? fields.generalLedger.label : "",
  },
  campaign: {
    enabled: fields.campaign.enabled,
    code: fields.campaign.enabled ? fields.campaign.code : "",
    label: fields.campaign.enabled ? fields.campaign.label : "",
  },
  thankYouLetter: {
    enabled: fields.thankYouLetter.enabled,
    code: fields.thankYouLetter.enabled ? fields.thankYouLetter.code : "",
    label: fields.thankYouLetter.enabled ? fields.thankYouLetter.label : "",
  },
});

export const EXTRA_GIFT_FIELD_LABELS: Record<ExtraGiftFieldKey, string> = {
  generalLedger: "General Ledger",
  campaign: "Campaign",
  thankYouLetter: "Thank You Letter",
};

export const ExtraGiftFieldsIntro: React.FC<{
  className?: string;
  showOptionalHint?: boolean;
}> = ({ className, showOptionalHint = true }) => (
  <p className={className}>
    Choose DonorPerfect General Ledger, Campaign or Thank You Letter codes to be
    attached to every ViewSpark donation that gets mirrored to DonorPerfect as a
    gift.
    {showOptionalHint ? (
      <>
        <br />
        <br />
        This step is optional, click next if you want to skip it.
      </>
    ) : null}
  </p>
);

export const formatCodeOptionLabel = (option: ICodeModel): string =>
  option.description?.trim() || option.code;

const sortCodeOptionsAlphabetically = (options: ICodeModel[]): ICodeModel[] =>
  [...options].sort((a, b) =>
    formatCodeOptionLabel(a).localeCompare(formatCodeOptionLabel(b), undefined, {
      sensitivity: "base",
    })
  );

export interface ExtraGiftFieldsSectionProps {
  extraGiftFields: ExtraGiftFieldsState;
  setExtraGiftFields: (fields: ExtraGiftFieldsState) => void;
  generalLedgers: ICodeModel[];
  campaigns: ICodeModel[];
  thankYouLetters: ICodeModel[];
  disabled: boolean;
  onValidationChange?: (valid: boolean) => void;
  showSectionHeader?: boolean;
}

const optionsForField = (
  key: ExtraGiftFieldKey,
  generalLedgers: ICodeModel[],
  campaigns: ICodeModel[],
  thankYouLetters: ICodeModel[]
): ICodeModel[] => {
  switch (key) {
    case "generalLedger":
      return sortCodeOptionsAlphabetically(generalLedgers);
    case "campaign":
      return sortCodeOptionsAlphabetically(campaigns);
    case "thankYouLetter":
      return sortCodeOptionsAlphabetically(thankYouLetters);
    default:
      return [];
  }
};

const ExtraGiftFieldColumn = ({
  fieldKey,
  label,
  setting,
  options,
  disabled,
  onChange,
}: {
  fieldKey: ExtraGiftFieldKey;
  label: string;
  setting: IExtraGiftFieldSetting;
  options: ICodeModel[];
  disabled: boolean;
  onChange: (updated: IExtraGiftFieldSetting) => void;
}) => (
  <Col md={4} sm={12} className={styles.extraGiftFieldColumn} key={fieldKey}>
    <div className={styles.extraGiftFieldHeader}>
      <Switch
        size="small"
        checked={setting.enabled}
        disabled={disabled}
        onChange={(_, checked) => onChange({ ...setting, enabled: checked })}
        color="primary"
      />
      <span className={styles.extraGiftFieldLabel}>{label}</span>
    </div>
    <div className={styles.extraGiftFieldSelect}>
      <CodeSelectDropdown
      value={setting.code}
      options={options.map(option => ({
        value: option.code,
        label: formatCodeOptionLabel(option),
      }))}
      disabled={disabled || !setting.enabled}
      onChange={code => {
        const selectedOption = options.find(option => option.code === code);
        onChange({
          ...setting,
          code,
          label: selectedOption ? formatCodeOptionLabel(selectedOption) : "",
        });
      }}
    />
    </div>
  </Col>
);

const ExtraGiftFieldsSection = ({
  extraGiftFields,
  setExtraGiftFields,
  generalLedgers,
  campaigns,
  thankYouLetters,
  disabled,
  onValidationChange,
  showSectionHeader = true,
}: ExtraGiftFieldsSectionProps) => {
  useEffect(() => {
    if (onValidationChange) {
      onValidationChange(isExtraGiftFieldsValid(extraGiftFields));
    }
  }, [extraGiftFields, onValidationChange]);

  const updateField = (
    key: ExtraGiftFieldKey,
    updated: IExtraGiftFieldSetting
  ) => {
    setExtraGiftFields({ ...extraGiftFields, [key]: updated });
  };

  return (
    <div className={styles.extraGiftFieldsSection}>
      {showSectionHeader && (
        <>
          <p className={styles.wizardHeading} style={{ marginTop: "1.5rem" }}>
            Extra Gift Fields
          </p>
          <p>
            Optionally attach DonorPerfect codes to every ViewSpark donation
            mirrored as a gift. Disabled fields are not sent.
          </p>
        </>
      )}
      <Row className={styles.extraGiftFieldsRow}>
        {(Object.keys(EXTRA_GIFT_FIELD_LABELS) as ExtraGiftFieldKey[]).map(
          key => (
            <ExtraGiftFieldColumn
              key={key}
              fieldKey={key}
              label={EXTRA_GIFT_FIELD_LABELS[key]}
              setting={extraGiftFields[key]}
              options={optionsForField(
                key,
                generalLedgers,
                campaigns,
                thankYouLetters
              )}
              disabled={disabled}
              onChange={updated => updateField(key, updated)}
            />
          )
        )}
      </Row>
    </div>
  );
};

export default ExtraGiftFieldsSection;
